// src/app/time-attendance/components/payroll-sync/pages/payable-hours-history/payable-hours-history.component.ts

import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, firstValueFrom } from 'rxjs';

import { PayrollSyncService } from '../services/payroll-sync.service';
import { PayableHourDto } from '../models/payroll-sync.model';
import { PayrollNavComponent } from '../../../shared/payroll-nav/payroll-nav.component';

type ToastKind = 'success' | 'error' | 'info';

@Component({
  selector: 'app-payable-hours-history',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, DecimalPipe, PayrollNavComponent],
  templateUrl: './payable-hours-history.component.html',
  styleUrls: ['./payable-hours-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PayableHoursHistoryComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  isLoading = true;
  errorMessage: string | null = null;

  toastMessage: string | null = null;
  toastKind: ToastKind = 'info';
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  rows: PayableHourDto[] = [];

  pageNumber = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages = 1;

  readonly pageSizeOptions = [10, 25, 50, 100];

  constructor(
    private readonly payrollSyncSvc: PayrollSyncService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    void this.loadHistory();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearToast();
  }

  async loadHistory(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;
    this.detect();

    try {
      const response = await firstValueFrom(
        this.payrollSyncSvc.getPayableHoursHistory({
          pageNumber: this.pageNumber,
          pageSize: this.pageSize,
        }),
      );

      this.rows = response.data ?? [];
      this.totalCount = Number(response.totalCount ?? 0);
      this.totalPages = Math.max(1, Number(response.totalPages ?? 1));
      this.pageNumber = Number(response.currentPage ?? this.pageNumber);
      this.pageSize = Number(response.pageSize ?? this.pageSize);
    } catch (error) {
      console.error('getPayableHoursHistory error', error);
      this.errorMessage = 'Failed to load payable hours history.';
      this.rows = [];
      this.totalCount = 0;
      this.totalPages = 1;
      this.showToast('error', 'Failed to load payable hours history.');
    } finally {
      this.isLoading = false;
      this.detect();
    }
  }

  refresh(): void {
    void this.loadHistory();
  }

  previousPage(): void {
    if (this.pageNumber <= 1 || this.isLoading) {
      return;
    }

    this.pageNumber -= 1;
    void this.loadHistory();
  }

  nextPage(): void {
    if (this.pageNumber >= this.totalPages || this.isLoading) {
      return;
    }

    this.pageNumber += 1;
    void this.loadHistory();
  }

  changePageSize(raw: string): void {
    const nextPageSize = Number(raw);

    if (!Number.isFinite(nextPageSize) || nextPageSize <= 0) {
      return;
    }

    this.pageSize = nextPageSize;
    this.pageNumber = 1;
    void this.loadHistory();
  }

  getEmployeeName(row: PayableHourDto): string {
    return `${row.employee?.firstName ?? ''} ${row.employee?.lastName ?? ''}`.trim() || '—';
  }

  getEmployeeNumber(row: PayableHourDto): string {
    return row.employee?.employeeNumber || '—';
  }

  getApplyDate(row: PayableHourDto): string | null {
    return row.applyDate ?? null;
  }

  getPayCodeLabel(row: PayableHourDto): string {
    const name = row.payCode?.name ?? '—';
    const type = row.payCode?.type ? ` (${row.payCode.type})` : '';

    return `${name}${type}`;
  }

  getPayGroupName(row: PayableHourDto): string {
    return row.payGroup?.name ?? '—';
  }

  getProjectName(row: PayableHourDto): string {
    return row.project?.name ?? '—';
  }

  getCompanyName(row: PayableHourDto): string {
    return row.company?.name ?? row.employee?.company?.name ?? '—';
  }

  getDepartmentLabel(row: PayableHourDto): string {
    const department = row.department ?? row.employee?.department ?? null;

    if (!department) {
      return '—';
    }

    const code = department.code !== null && department.code !== undefined ? String(department.code) : '';
    const name = department.name ?? '';

    if (code && name) {
      return `${code} - ${name}`;
    }

    return code || name || '—';
  }

  getWorkerTypeName(row: PayableHourDto): string {
    return row.workerType?.name ?? '—';
  }

  getWorkOrderLabel(row: PayableHourDto): string {
    if (!row.workOrderNumber) {
      return '—';
    }

    const won = row.workOrderNumber.won ?? '';
    const description = row.workOrderNumber.description ?? '';

    if (won && description) {
      return `${won} - ${description}`;
    }

    return won || description || '—';
  }

  getYywwLabel(row: PayableHourDto): string {
    return row.yyww?.name ?? (row.yywwId ? String(row.yywwId) : '—');
  }

  getPayableHoursSyncId(row: PayableHourDto): string {
    const id = row.payableHoursSyncId ?? row.payableHoursSync?.id;

    return id ? String(id) : '—';
  }

  getSyncCreatedBy(row: PayableHourDto): string {
    const createdBy = row.createdBy ?? row.payableHoursSync?.createdBy ?? null;

    if (!createdBy) {
      return '—';
    }

    return `${createdBy.firstName ?? ''} ${createdBy.lastName ?? ''}`.trim() || '—';
  }

  getSyncCreatedAt(row: PayableHourDto): string | null {
    return row.createdAt ?? row.payableHoursSync?.createdAt ?? null;
  }

  clearToast(): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    this.toastTimer = null;
    this.toastMessage = null;
    this.detect();
  }

  private showToast(kind: ToastKind, message: string): void {
    this.toastKind = kind;
    this.toastMessage = message;

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    this.toastTimer = setTimeout(() => this.clearToast(), 2600);
    this.detect();
  }

  private detect(): void {
    if (!(this.cdr as unknown as { destroyed?: boolean }).destroyed) {
      this.cdr.detectChanges();
    }
  }

  trackById(_: number, item: { id: number }): number {
    return item.id;
  }
}